// GAVION recruiting (spec docs/superpowers/specs/2026-10-27-recruiting-design.md): shapes and rules shared by the
// « Recrutement » screens and the in-memory client. SQL twins: supabase/schemas/91_gavion_recruiting.sql
// (private.recruiting_slugify, private.recruiting_free_slug, private.job_applications_before).
import type { AiReview, AiReviewSummary } from "./aiReview";
import type { ApplicationSource } from "./manualCandidate";

export const POSTING_STATUSES = ["draft", "published", "closed"] as const;
export type PostingStatus = (typeof POSTING_STATUSES)[number];
export const EMPLOYMENT_TYPES = [
  "full_time",
  "part_time",
  "contract",
  "commission",
  "internship",
] as const;
export type EmploymentType = (typeof EMPLOYMENT_TYPES)[number];
export const REMOTE_MODES = ["onsite", "hybrid", "remote"] as const;
export type RemoteMode = (typeof REMOTE_MODES)[number];
export const APPLICATION_STATUSES = [
  "new",
  "interview",
  "offer",
  "hired",
  "rejected",
] as const;
export type ApplicationStatus = (typeof APPLICATION_STATUSES)[number];

/** The posting title stored on an application sent without a (published) posting. */
export const SPONTANEOUS_TITLE = "Candidature spontanée";
export const PUBLIC_SITE = "https://gavion.ai";
/** The site serves the French posting pages under /fr/carrieres (/carrieres only redirects). */
export const publicPostingUrl = (slug: string) =>
  `${PUBLIC_SITE}/fr/carrieres/${slug}`;

/** The editable fields of a posting (spec §2). Blank optional texts are stored as null. */
export type PostingInput = {
  slug: string;
  title_fr: string;
  title_en: string;
  employment_type: EmploymentType;
  location: string;
  remote: RemoteMode;
  description_fr: string;
  description_en: string;
  requirements_fr: string;
  requirements_en: string;
  pay_text_fr: string;
  pay_text_en: string;
};
export type Posting = {
  id: number;
  uid: string;
  slug: string;
  status: PostingStatus;
  title_fr: string;
  title_en: string | null;
  employment_type: EmploymentType;
  location: string | null;
  remote: RemoteMode;
  description_fr: string;
  description_en: string | null;
  requirements_fr: string | null;
  requirements_en: string | null;
  pay_text_fr: string | null;
  pay_text_en: string | null;
  published_at: string | null;
  closed_at: string | null;
  created_at: string;
  updated_at: string;
  applications: number;
  new_applications: number;
};
export type ApplicationSummary = {
  id: number;
  uid: string;
  posting_id: number | null;
  posting_title: string;
  posting_slug: string | null;
  name: string;
  /** GAVION manual-candidates: a manual row (Indeed, referral, other) has an email or a phone, maybe not both. */
  email: string | null;
  phone: string | null;
  phone_e164: string | null;
  language: "fr" | "en";
  /** Owner ruling 2026-10-27: when the candidate is available for an interview (required from the site, free text). */
  availability: string | null;
  /** GAVION manual-candidates: website (the site's form) or a manual source (« Ajouter un candidat »). */
  source: ApplicationSource;
  /** GAVION manual-candidates: the candidate's profile link (https), manual rows only. */
  profile_url: string | null;
  status: ApplicationStatus;
  has_cv: boolean;
  cv_filename: string | null;
  cv_size: number | null;
  /** Owner ruling 2026-10-27: an optional cover letter file. */
  has_letter: boolean;
  letter_filename: string | null;
  letter_size: number | null;
  consent_at: string;
  created_at: string;
  purge_after: string | null;
  /** GAVION ai-hiring-review: the AI review's status, score and verdict (null: none yet). */
  ai_review: AiReviewSummary | null;
};
export type ApplicationNote = {
  id: number;
  text: string;
  created_at: string;
  sales_id: number | null;
  author: string | null;
};
export type ApplicationDetail = Omit<ApplicationSummary, "ai_review"> & {
  message: string | null;
  cv_path: string | null;
  letter_path: string | null;
  notes: ApplicationNote[];
  /** GAVION ai-hiring-review: the whole AI review (null: none yet). */
  ai_review: AiReview | null;
};
export type ApplicationFilter = {
  postingId?: number | null;
  spontaneous?: boolean;
  status?: ApplicationStatus | null;
};

export const SLUG_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;
export const SLUG_MAX = 80;

/**
 * Slug from a French title: accents dropped, œ → oe, æ → ae, lower case, every other run of characters → "-", 80
 * characters at most, no dash at either end. Twin of private.recruiting_slugify (vectors in recruiting.test.sql).
 */
export function slugify(title: string): string {
  return title
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replaceAll("œ", "oe")
    .replaceAll("æ", "ae")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, SLUG_MAX)
    .replace(/-+$/, "");
}

/** A free slug: base, else base-2, base-3, … (twin of private.recruiting_free_slug). */
export function freeSlug(base: string, taken: ReadonlySet<string>): string {
  const b = base || "poste";
  let slug = b;
  for (let n = 2; taken.has(slug); n++)
    slug = `${b.slice(0, SLUG_MAX - String(n).length - 1).replace(/-+$/, "")}-${n}`;
  return slug;
}

const addMonths = (d: Date, n: number) => {
  const x = new Date(d.getTime());
  x.setUTCMonth(x.getUTCMonth() + n);
  return x;
};
const DAY_MS = 86_400_000;

/**
 * purge_after after a status change (spec §2): « Embauché » clears it; leaving « Embauché » gives created_at + 12
 * months, or now + 30 days when that is already past; any other change keeps the current value. Twin of
 * private.job_applications_before.
 */
export function purgeAfterFor(
  createdAt: Date,
  from: ApplicationStatus,
  to: ApplicationStatus,
  current: Date | null,
  now: Date,
): Date | null {
  if (from === to) return current;
  if (to === "hired") return null;
  if (from !== "hired") return current;
  const twelve = addMonths(createdAt, 12);
  return twelve > now ? twelve : new Date(now.getTime() + 30 * DAY_MS);
}
export const initialPurgeAfter = (createdAt: Date) => addMonths(createdAt, 12);

/** The posting actions shown for a status: « Publier », « Fermer », « Republier ». */
export function postingActions(
  status: PostingStatus,
): ("publish" | "close" | "republish")[] {
  if (status === "draft") return ["publish"];
  if (status === "published") return ["close"];
  return ["republish"];
}
/** « Supprimer »: a draft with no application only. */
export const postingDeletable = (p: Pick<Posting, "status" | "applications">) =>
  p.status === "draft" && p.applications === 0;

export const EMPTY_POSTING: PostingInput = {
  slug: "",
  title_fr: "",
  title_en: "",
  employment_type: "full_time",
  location: "",
  remote: "onsite",
  description_fr: "",
  description_en: "",
  requirements_fr: "",
  requirements_en: "",
  pay_text_fr: "",
  pay_text_en: "",
};
export const postingToInput = (p: Posting): PostingInput => ({
  slug: p.slug,
  title_fr: p.title_fr,
  title_en: p.title_en ?? "",
  employment_type: p.employment_type,
  location: p.location ?? "",
  remote: p.remote,
  description_fr: p.description_fr,
  description_en: p.description_en ?? "",
  requirements_fr: p.requirements_fr ?? "",
  requirements_en: p.requirements_en ?? "",
  pay_text_fr: p.pay_text_fr ?? "",
  pay_text_en: p.pay_text_en ?? "",
});
